import { Trans, useLingui } from '@lingui/react/macro'
import {
  type PropertySubscription,
  type PropertyWithRelations,
} from '@markku/types'
import { Alert, Stack, Text, Title } from '@mantine/core'
import { IconInfoCircle } from '@tabler/icons-react'
import { PackageCard } from './PackageCard'

// Type for service packages from listActive endpoint
interface ServicePackageVersion {
  id: number
  priceMonthly: string
  description: string | null | undefined
  isActive: boolean
  packageVersionTasks?: Array<{
    id: number
    frequency: string | null | undefined
    maintenanceTask?: {
      id: number
      name: string
    }
  }>
}

interface ServicePackage {
  id: number
  name: string
  description: string | null
  versions?: ServicePackageVersion[]
}

interface PackageListSectionProps {
  packages: ServicePackage[] | undefined
  property: PropertyWithRelations | undefined
  activeSubscription: PropertySubscription | undefined
  proposalSubscription: PropertySubscription | undefined
  isCreatingCheckout: boolean
  onSubscribe: (versionId: number) => void
  canSubscribe?: boolean
}

export function PackageListSection({
  packages,
  property,
  activeSubscription,
  proposalSubscription: _proposalSubscription,
  isCreatingCheckout,
  onSubscribe,
  canSubscribe = true,
}: PackageListSectionProps) {
  const { t } = useLingui()
  // Check if property has floor area
  const existingData =
    property?.data && typeof property.data === 'object' ? property.data : {}
  const hasFloorArea =
    'floorArea' in existingData &&
    existingData.floorArea !== null &&
    existingData.floorArea !== undefined &&
    existingData.floorArea !== ''

  return (
    <Stack gap="lg">
      {activeSubscription && (
        <>
          <Title order={3}>
            <Trans>Change package</Trans>
          </Title>
          <Text>
            <Trans>You can change to another package at any time.</Trans>
          </Text>
        </>
      )}

      {!activeSubscription && (
        <>
          <Title order={3}>
            <Trans>Select package</Trans>
          </Title>
          <Text>
            <Trans>
              Select a suitable Markku package for your property. We handle all
              necessary maintenance and keep your property in good condition.
            </Trans>
          </Text>
        </>
      )}

      {packages?.map((pkg) => {
        const activeVersions = pkg.versions?.filter((v) => v.isActive)

        return (
          <div key={pkg.id}>
            {activeVersions?.map((version) => {
              const activePackageVersionId =
                activeSubscription?.subscriptionPackageVersions?.[0]
                  ?.servicePackageVersion?.id
              const isCurrentPackage = activePackageVersionId === version.id

              return (
                <PackageCard
                  key={version.id}
                  packageName={pkg.name}
                  packageDescription={pkg.description}
                  version={version}
                  isCurrent={isCurrentPackage}
                  isLoading={isCreatingCheckout}
                  onSubscribe={onSubscribe}
                  canSubscribe={canSubscribe}
                />
              )
            })}
          </div>
        )
      })}

      {!hasFloorArea && (
        <Alert
          color="blue"
          variant="light"
          icon={<IconInfoCircle />}
          title={t`Do you only see one package?`}
        >
          <Text size="sm">
            <Trans>
              Enter your property's total floor area in the card above, and
              we'll show you all suitable Markku packages.
            </Trans>
          </Text>
        </Alert>
      )}
    </Stack>
  )
}
